import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { guardiansApi } from '@/services/api/endpoints/guardians';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import { humanise, orDash } from '@/utils/format';
import { GuardianFormModal, toPayload } from './GuardianForm';
import { PortalAccountPanel } from '@/features/students/PortalAccountPanel';

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}

/**
 * One guardian, and every child they are responsible for.
 *
 * The children list is the screen's reason to exist: it makes visible that this
 * is one shared record rather than a copy per student, so an edit here is
 * understood to reach all of them.
 */
export function GuardianDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);

  const guardian = useQuery({
    queryKey: queryKeys.guardians.detail(id),
    queryFn: () => guardiansApi.get(id),
    enabled: id !== '',
  });

  const children = useQuery({
    queryKey: queryKeys.guardians.students(id),
    queryFn: () => guardiansApi.students(id),
    enabled: id !== '',
  });

  const update = useMutation({
    mutationFn: (payload: Parameters<typeof guardiansApi.update>[1]) => guardiansApi.update(id, payload),
    onSuccess: () => {
      toast.success('Guardian updated for every linked child');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.guardians.all });
    },
  });

  const changeStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'INACTIVE') => guardiansApi.changeStatus(id, status),
    onSuccess: (updated) => {
      toast.success(`Guardian marked ${updated.status.toLowerCase()}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.guardians.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (guardian.isPending) {
    return <LoadingState label="Loading guardian" />;
  }
  if (guardian.error !== null || guardian.data === undefined) {
    return <ErrorState error={guardian.error} onRetry={() => void guardian.refetch()} />;
  }

  const record = guardian.data;

  return (
    <div className="space-y-6">
      <Link to={ROUTES.guardians} className="mb-3 inline-block text-sm text-muted hover:text-ink">
        ← Back to guardians
      </Link>

      <PageHeader
        title={record.fullName}
        description="A shared record. Changes here reach every child this guardian is linked to."
        action={
          can(PERMISSIONS.GUARDIAN_UPDATE) ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                variant="ghost"
                loading={changeStatus.isPending}
                onClick={() => changeStatus.mutate(record.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
              >
                {record.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Contact details" />
          <dl className="grid gap-4 sm:grid-cols-2">
            <Detail label="Phone" value={record.phone} />
            <Detail label="Alternate phone" value={orDash(record.alternatePhone)} />
            <Detail label="E-mail" value={orDash(record.email)} />
            <Detail label="National ID" value={orDash(record.nationalId)} />
            <Detail label="Occupation" value={orDash(record.occupation)} />
            <Detail label="Employer" value={orDash(record.employer)} />
            <Detail label="Nationality" value={orDash(record.nationality)} />
            <div>
              <dt className="text-xs text-subtle">Status</dt>
              <dd className="mt-1">
                <StatusBadge status={record.status} />
              </dd>
            </div>
          </dl>
        </Card>

        <Card>
          <CardHeader
            title="Children"
            description="Every student this guardian is responsible for."
          />

          {children.isPending ? (
            <LoadingState label="Loading children" />
          ) : children.data === undefined || children.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-subtle">
              Not linked to any student yet. Link them from a student’s record.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {children.data.map((link) => (
                <li key={link.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <Link
                      to={ROUTES.studentDetail(link.studentId)}
                      className="block truncate text-sm font-medium text-ink hover:text-accent"
                    >
                      {link.studentName ?? 'Student'}
                    </Link>
                    <p className="text-xs text-subtle">{humanise(link.relationshipType)}</p>
                  </div>
                  {link.primaryGuardian && <Badge tone="accent">Primary</Badge>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <PortalAccountPanel
          subject="this guardian"
          role="PARENT"
          queryKey={queryKeys.guardians.account(id)}
          load={() => guardiansApi.account(id)}
          link={(userId) => guardiansApi.linkAccount(id, userId)}
          unlink={() => guardiansApi.unlinkAccount(id)}
        />
      </div>

      <GuardianFormModal
        open={editing}
        guardian={record}
        saving={update.isPending}
        onClose={() => setEditing(false)}
        onSubmit={(values, setError) =>
          update.mutate(toPayload(values), { onError: (error) => handleApiError(error, setError) })
        }
      />
    </div>
  );
}
